import { Component, DestroyRef, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TableModule, TableLazyLoadEvent } from "primeng/table";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";
import { SkeletonModule } from "primeng/skeleton";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { ButtonModule } from "primeng/button";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { debounceTime, forkJoin, Subject } from "rxjs";

import { ClientGroupService } from "../../../core/services/client-group.service";
import { ClientGroupSummaryDto } from "../../../core/models/client-group/client-group-summary.dto";
import { GenericFilterRequestDto } from "../../../core/models/generic-filter-request.dto";
import { GenericSortRequestDto } from "../../../core/models/generic-sort-request.dto";
import { SpinnerComponent } from "../../../shared/components/spinner/spinner.component";
import { ModalStackerComponent } from "../../../shared/components/modal-stacker/modal-stacker.component";
import { ClientGroupDetailComponent } from "../client-group-detail/client-group-detail.component";
import { JniToastrService } from "../../../core/services/jni-toastr.service";

@Component({
  selector: "jni-client-group",
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    CardModule,
    InputTextModule,
    SkeletonModule,
    ToggleSwitchModule,
    ButtonModule,
    SpinnerComponent,
    ModalStackerComponent,
    ClientGroupDetailComponent,
  ],
  templateUrl: "./client-group.component.html",
  styleUrl: "./client-group.component.scss",
})
export class ClientGroupComponent implements OnInit {
  clientGroups: ClientGroupSummaryDto[] = [];
  totalRecords = 0;
  loading = true;
  rows = 50;

  /** Clients selected via the row checkboxes for the bulk enable action. */
  selectedClients: ClientGroupSummaryDto[] = [];
  /** Whether a bulk enable request is in progress. */
  enablingSelected = false;

  showDetailStacker = false;
  selectedClientId = "";
  selectedClientName = "";

  filterTextChanged: Subject<TableLazyLoadEvent> =
    new Subject<TableLazyLoadEvent>();
  filterDebounceTime = 500;
  private lastFiltersJson = "";
  private isInitialLoad = true;

  constructor(
    private _clientGroupService: ClientGroupService,
    private _destroyRef: DestroyRef,
    private _toastr: JniToastrService
  ) {}

  ngOnInit(): void {
    this.filterTextChanged
      .pipe(
        debounceTime(this.filterDebounceTime),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(event => this.callApi(event));

    // The initial load is driven by the table's (onLazyLoad) event, which PrimeNG emits once on
    // initialisation. Triggering loadData here as well caused the client group list API to be
    // called twice on first render, so the explicit initial call is intentionally omitted.
  }

  loadData(event: TableLazyLoadEvent): void {
    this.loading = true;
    const filtersJson = JSON.stringify(event.filters ?? {});
    const isFilterChange =
      filtersJson !== this.lastFiltersJson && !this.isInitialLoad;
    this.lastFiltersJson = filtersJson;
    this.isInitialLoad = false;

    if (isFilterChange) {
      this.filterTextChanged.next(event);
    } else {
      this.callApi(event);
    }
  }

  private callApi(event: TableLazyLoadEvent): void {
    this.loading = true;

    const skip = event.first ?? 0;
    const take = event.rows ?? this.rows;

    const filters: GenericFilterRequestDto[] = [];
    if (event.filters) {
      for (const [key, filterMeta] of Object.entries(event.filters)) {
        const meta = Array.isArray(filterMeta) ? filterMeta[0] : filterMeta;
        if (meta?.value) {
          filters.push({
            columnName: key,
            value: meta.value,
            matchMode: meta.matchMode ?? "contains",
            tableName: "",
          });
        }
      }
    }

    const sort: GenericSortRequestDto[] = [];
    if (event.sortField) {
      sort.push({
        field: event.sortField as string,
        order: event.sortOrder ?? 0,
      });
    }

    this._clientGroupService
      .getClientsWithGroupCounts(skip, take, filters, sort)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: response => {
          this.clientGroups = response.data;
          this.totalRecords = response.totalRecords ?? 0;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  /**
   * Enables or disables a client's Client Portal access. Optimistically updates the row and
   * reverts the toggle when the persist call fails.
   * @param item The client row whose access is changing.
   * @param isEnabled `true` to enable Client Portal access; otherwise, `false`.
   */
  onToggleEnable(item: ClientGroupSummaryDto, isEnabled: boolean): void {
    this._clientGroupService
      .updateClientPortalAccess(item.customerAccount, isEnabled)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          item.isEnabled = isEnabled;
          this._toastr.success(
            isEnabled
              ? "Client Portal access enabled successfully"
              : "Client Portal access disabled successfully"
          );
        },
        error: () => {
          item.isEnabled = !isEnabled;
          this._toastr.error("Unable to update access. Please try again.");
        },
      });
  }

  /**
   * Enables Client Portal access for all selected clients in a single action. Skips clients that
   * are already enabled and clears the selection on success.
   */
  enableSelected(): void {
    const toEnable = this.selectedClients.filter(client => !client.isEnabled);

    if (toEnable.length === 0) {
      this._toastr.info("Selected clients are already enabled.");
      return;
    }

    this.enablingSelected = true;
    forkJoin(
      toEnable.map(client =>
        this._clientGroupService.updateClientPortalAccess(
          client.customerAccount,
          true
        )
      )
    )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          toEnable.forEach(client => (client.isEnabled = true));
          this.selectedClients = [];
          this.enablingSelected = false;
          this._toastr.success(
            `${toEnable.length} client(s) enabled successfully`
          );
        },
        error: () => {
          this.enablingSelected = false;
          this._toastr.error(
            "Unable to enable the selected clients. Please try again."
          );
        },
      });
  }

  openDetailStacker(clientId: string, clientName: string): void {
    this.selectedClientId = clientId;
    this.selectedClientName = clientName;
    this.showDetailStacker = true;
  }

  closeDetailStacker(): void {
    this.showDetailStacker = false;
    this.selectedClientId = "";
    this.selectedClientName = "";
  }
}
